import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import doodlePattern from '../../doodle-pattern.svg';
import { API_URL } from '../../config/api';
import { addMessage } from '../../store/slices/chatSlice';
import { commentKey, mergeComments } from '../../utils/threadCommentUtils';

const ThreadPanel = ({
  channel,
  user,
  isReadOnlyChannel,
  activeThreadId,
  rawChannelMessages = [],
  onClose,

  // Handlers from parent for messaging actions
  handleSendMessage,
  handleTyping,
  handleAddReaction,
  handleEditMessage,
  handleDeleteMessage,
  handleRetryMessage,
  scrollToMessage,
  messageRefs,
  containerRef,

  // Forward message modal states
  setMessageToForward,
  setShowForwardModal,

  // State from parent
  initialReplyingToMessage = null,
  dailyTasksOpen = false,
  isPreview = false,
}) => {
  const dispatch = useDispatch();
  const threadContainerRef = useRef(null);
  const threadEndRef = useRef(null);
  const [replyingToMessage, setReplyingToMessage] = useState(initialReplyingToMessage);
  const [fetchedReplies, setFetchedReplies] = useState([]);

  const setThreadContainerRef = (el) => {
    threadContainerRef.current = el;
    if (!containerRef) return;
    if (typeof containerRef === 'function') {
      containerRef(el);
    } else {
      containerRef.current = el;
    }
  };

  const isDirectMessage = channel.type === 'direct' || channel.type === 'DIRECT_MESSAGE';

  useEffect(() => {
    if (!activeThreadId) {
      setFetchedReplies([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/messages/${activeThreadId}/replies`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        if (cancelled || !Array.isArray(data)) return;
        setFetchedReplies(data);
        data.forEach((msg) => {
          const channelId = msg?.channelId || channel?.id;
          if (!channelId || !msg?.id) return;
          dispatch(addMessage({
            channelId,
            message: msg,
            skipUnreadCountIncrement: true,
          }));
        });
      } catch {
        // Keep local channel messages if replies endpoint fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeThreadId, channel?.id, dispatch]);

  // Parent + replies (from loaded channel history and /replies fetch).
  const threadMessages = useMemo(() => {
    if (!activeThreadId) return [];
    const rootId = String(activeThreadId);
    const local = [];
    const threadMessageIds = new Set([rootId]);
    let addedNew;
    do {
      addedNew = false;
      for (const m of rawChannelMessages) {
        if (m?.replyToMessageId && threadMessageIds.has(String(m.replyToMessageId)) && !threadMessageIds.has(String(m.id))) {
          threadMessageIds.add(String(m.id));
          addedNew = true;
        }
      }
    } while (addedNew);

    rawChannelMessages.forEach((m) => {
      if (m?.id && threadMessageIds.has(String(m.id))) local.push(m);
    });

    const merged = mergeComments(local, fetchedReplies);
    // Keep root first, then chronological replies.
    const root = merged.find((m) => commentKey(m) === rootId)
      || rawChannelMessages.find((m) => String(m.id) === rootId)
      || null;
    const replies = merged.filter((m) => commentKey(m) !== rootId);
    return root ? [root, ...replies] : replies;
  }, [rawChannelMessages, activeThreadId, fetchedReplies]);

  const activeThreadParentMessage = useMemo(() => {
    if (!activeThreadId) return null;
    return threadMessages.find((m) => String(m.id) === String(activeThreadId))
      || rawChannelMessages.find((m) => String(m.id) === String(activeThreadId))
      || null;
  }, [rawChannelMessages, activeThreadId, threadMessages]);

  const threadRepliesCount = useMemo(() => {
    if (!activeThreadId || threadMessages.length === 0) return 0;
    return threadMessages.filter((m) => String(m.id) !== String(activeThreadId)).length;
  }, [threadMessages, activeThreadId]);

  const threadParticipantsCount = useMemo(() => {
    if (!activeThreadId || threadMessages.length <= 1) return 0;
    const replies = threadMessages.filter((m) => String(m.id) !== String(activeThreadId));
    const seen = new Set();
    for (const r of replies) {
      const identifier = r.senderId || r.senderName || 'unknown';
      seen.add(identifier);
    }
    return seen.size;
  }, [threadMessages, activeThreadId]);

  useEffect(() => {
    // Scroll to bottom of thread when new thread messages arrive
    if (threadContainerRef.current && threadEndRef.current) {
      const container = threadContainerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;

      // Auto-scroll if near bottom or if we just opened the thread
      if (isNearBottom || threadMessages.length <= 15) {
        setTimeout(() => {
          threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [threadMessages.length, activeThreadId]);

  // Sync replyingToMessage when it changes in parent
  useEffect(() => {
    setReplyingToMessage(initialReplyingToMessage);
  }, [initialReplyingToMessage]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800">
      {/* Thread Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Reply in Thread</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {threadRepliesCount > 0
              ? `${threadRepliesCount} ${threadRepliesCount === 1 ? 'reply' : 'replies'} from ${threadParticipantsCount} ${threadParticipantsCount === 1 ? 'person' : 'people'}`
              : activeThreadParentMessage?.senderName
                ? `replies to ${activeThreadParentMessage.senderName}`
                : ''}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Close thread"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Thread Messages */}
      <div
        ref={setThreadContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 relative chat-window-pattern"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 dark:hidden opacity-30"
          style={{
            backgroundImage: `url(${doodlePattern})`,
            backgroundSize: '150px 150px',
            backgroundRepeat: 'repeat',
            backgroundPosition: '0 0',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 hidden dark:block opacity-10"
          style={{
            backgroundImage: `url(${doodlePattern})`,
            backgroundSize: '150px 150px',
            backgroundRepeat: 'repeat',
            backgroundPosition: '0 0',
            filter: 'invert(1)',
          }}
        />
        <div className="relative z-10">
          <MessageList
            messages={threadMessages}
            currentUserId={user?.id}
            isDirectMessage={isDirectMessage}
            onAddReaction={handleAddReaction}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            onReply={(message) => {
              setReplyingToMessage(message);
            }}
            onViewThread={null} // Disallow deeper nesting and hide nested replies button
            onForward={(message) => {
              setMessageToForward(message);
              setShowForwardModal(true);
            }}
            onScrollToMessage={scrollToMessage}
            messageRefs={messageRefs}
            onRetryMessage={handleRetryMessage}
            reminderPanelOpen={dailyTasksOpen}
            allChannelMessages={rawChannelMessages}
            channelId={channel.id}
          />
          <div ref={threadEndRef} />
        </div>
      </div>

      {/* Thread Input */}
      {!isReadOnlyChannel && (
        <MessageInput
          key={`thread-${activeThreadId}-${replyingToMessage?.id || 'parent'}`}
          onSendMessage={(content, attachments, replyToMessageId, linkPreview, mentionedUserIds, attachmentGroupName, targetResourceGroupId, sensitiveMeta) =>
            handleSendMessage(content, attachments, replyToMessageId || activeThreadId, linkPreview, mentionedUserIds, attachmentGroupName, targetResourceGroupId, sensitiveMeta)
          }
          onTyping={handleTyping}
          channelId={channel.id}
          replyToMessage={replyingToMessage || activeThreadParentMessage}
          onCancelReply={() => setReplyingToMessage(null)}
          isThreadInput={true}
          autoFocusComposer={!isPreview}
        />
      )}
    </div>
  );
};

export default ThreadPanel;
